import { Injectable } from '@angular/core';
import { User } from '@app/models';
import { DevelopmentCalendarPayments, UserStatusModel } from '@interfaces/app';
import { differenceInMonths, fromUnixTime, isSameMonth } from 'date-fns';
import { ImageOptimizerService } from '../image-optimizer/image-optimizer.service';
@Injectable({
  providedIn: 'root'
})
export class UtilsV2Service {

  actualStatusUser: UserStatusModel;
  public readonly urlBaseS3 = 'https://static.monific.com/assets/projects-images/';

  constructor(private imageOptimizerServ: ImageOptimizerService) { }

  /** Definir que dashboard se debe mostrar dependiendo el paso del usuario */
  defineDashboardToShow(user: User) {
  
    const { stepRegister, participations, autoinvest, domiciliation, hasSpoIn } = user;
  
    // Inicializar valores predeterminados
    const defaultStatus = {
      isLead: false,
      isInvestor: false,
      isNotInvestor: false,
      isFullGrowthLead: false,
      isFullDomLead: false,
      isFullLead: false,
      hasSpoIn: false
    };
  
    // Leads
    if (stepRegister <= 4) {
      this.actualStatusUser = { ...defaultStatus, isLead: true };
      return this.actualStatusUser;
    }
  
    // Usuario con registro inicial completo
    if (stepRegister >= 5) {
      if (!participations || participations.length === 0) {
        this.actualStatusUser = { ...defaultStatus, isNotInvestor: true };
        if (hasSpoIn) {
          this.actualStatusUser.hasSpoIn = user?.hasSpoIn ? true : false;
        }
        return this.actualStatusUser;
      }
      // Verificar autoinversión
      const hasAutoInvest = autoinvest?.status === 'ACTIVE';
      const hasDomiciliation = domiciliation?.status === 'ACTIVE';
  
      this.actualStatusUser = {
        ...defaultStatus,
        isInvestor: true,
        isFullGrowthLead: hasAutoInvest,
        isFullDomLead: hasDomiciliation,
        isFullLead: hasAutoInvest && hasDomiciliation,
        hasSpoIn: hasSpoIn
      };
  
      return this.actualStatusUser;
    }
  
    return defaultStatus;
  }

  /** Capitalizar cualquier palabra */
  formatName(name: string): string {
    return name?.toLowerCase()?.replace(/\b\w/g, (c) => c?.toUpperCase());
  }

  /** Función para armar automaticamente la URL de los proyectos */
  buildGalleryImageUrl(basePath: string, nameImg: string, width?: number, quality?: number): string {
    let fullUrl = `${this.urlBaseS3}${basePath}/${nameImg}`;
    const assetPrefix = 'https://static.monific.com/assets/';
    
    const parts = fullUrl.split(assetPrefix);
    if (parts.length > 2) {
      fullUrl = assetPrefix + parts.pop();
    }

    width = width ? width : 950;
  
    return this.imageOptimizerServ.getOptimizedImageUrl(fullUrl, width, quality);
  }
  

  /** Función para dividir nombre - campaña */
  getNameSplit(name: string): string[] {
    if(name == null || name === undefined) {
      return ['', '']
    } else {
      const [devName, phase = '1'] = name?.split(' - ');
      return [devName, phase];
    }
  }  

  /** Función para obtener los meses restantes entre 2 fechas
   * @param startDateMs - Fecha de inicio en milisegundos
   * @param totalMonths - Total de meses a calcular
   */
  getRemainingMonths(startDateMs: number, totalMonths: number): number {
    const startDate = new Date(startDateMs);
    const now = new Date();
    const msInMonth = 1000 * 60 * 60 * 24 * 30.44;
  
    const diffInMs = now.getTime() - startDate.getTime();
    const monthsElapsed = Math.floor(diffInMs / msInMonth);
  
    const remaining = totalMonths - monthsElapsed;
    return remaining;
  }

  /** Función para sacar de la propieades 'monthsEstimateResidential' el numero */
  getNumberFromString(str: string): number {
    const regex = /\d+/;
    const match = str?.match(regex);
    return match ? parseInt(match?.[0], 10) : 0;
  };

  /** Función para calcular la diff de meses desde una fecha */
  calculateMonthsDiff(startDateMs: number): number {
    return differenceInMonths(new Date(), new Date(startDateMs));
  }

  /** Función para sumar distribuciones o participaciones - etc */
  sumDistributions(distributions: any[]): number {
    return distributions.reduce((acc, distribution) => {
      const amount = parseFloat(distribution?.amount || 0);
      return acc + amount;
    }, 0);
  }

  /** Función para saber si el mes es el mismo al paymentDate */
  filterDevelopmentPaymentsByCurrentMonth(data: DevelopmentCalendarPayments[]): DevelopmentCalendarPayments[] {
    const now = new Date();
  
    return data.map(project => {
      const filteredPayments = project.calendarPayments.filter(payment => {
        const date = fromUnixTime(Math.floor(payment.paymentDate / 1000));
        return isSameMonth(date, now);
      });
  
      return {
        ...project,
        calendarPayments: filteredPayments
      };
    });
  }

  /** Función para rendimientos o numeros y truncarlos */
  truncateToTwoDecimals(value: number): number {
    return Math.floor(value * 100) / 100;
  }

  /** Función para optmizar imagenes con base al cloudflare */
  optimizationImageUrlHTML(url: string, width?: number, quality?: number): string {
    return this.imageOptimizerServ.getOptimizedImageUrl(url, width ? width : 950, quality);
  }

  /** Función para regresar el numero dependiendo el tipo de proyecto */
  getCapitalization(capitalization: string, monthsLimit: string): number {
    switch (capitalization) {
      case 'Mensual':
        return this.getNumberFromString(monthsLimit);
      case 'Bimestral':
        return this.getNumberFromString(monthsLimit) / 2;
      case 'Trimestral':
        return this.getNumberFromString(monthsLimit) / 3;
      case 'Cuatrimestral':
        return this.getNumberFromString(monthsLimit) / 4;
      case 'Semestral':
        return this.getNumberFromString(monthsLimit)/ 6;
      case 'Anual':
        return this.getNumberFromString(monthsLimit) / 12;
      default:
        this.getNumberFromString(monthsLimit);
    }
    return 0;
  }


}
